/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useState } from 'react';

// Mantine
import { DataTable as NativeDataTable } from 'mantine-datatable';
import {
	Image,
	Text,
	Box,
	Tooltip as NativeTooltip,
	Group,
} from '@mantine/core';

// Styles
import classes from './Datatable.module.css';

// Cells
import { VerifiedButton } from '@components/atoms/VerifiedButton';
import { CellProductionsActions } from './cells/CellProductionsActions';
import { CellOffsetsActions } from './cells/CellOffsetsActions';
import { CellPublicOffsetsActions } from './cells/CellPublicOffsetsActions';
import { CellMintStatus } from './cells/CellMintStatus/';
import { CellApiTokensActions } from './cells/CellApiTokensActions';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { CellProductionsStartMintActions } from './cells/CellProductionsStartMintActions';
import { CellProductionsStartMintWithoutRemoveActions } from './cells/CellProductionsStartMintWithoutRemoveActions';
import clsx from 'clsx';
import { IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import './DatatableCustomHeaderStyle.css';
import { CellConversionFactorsActions } from './cells/CellConversionFactorsActions/CellConversionFactorsActions';
import { CellConversionFactorStatus } from './cells/CellConversionFactorStatus';

import { Tooltip } from '@components/atoms';
import { NOT_APPLICABLE } from '@config/constants/common';
import { CellShortText } from './cells/CellShortText';
import { CellSitesActions } from './cells/CellSitesActions';
import isNil from 'lodash/isNil';
import { CellGlobalActions } from './cells/CellGlobalActions';
import { CellTags } from './cells/CellTags/CellTags';
import { CellMeasurabilitiesActions } from './cells/CellMeasurabilitiesActions';
import { CellOverrangeStatus } from './cells/CellOverrangeStatus';
import { TOffset } from '@features/offsets/types';
import { LanguageDisplay } from '@components/atoms/LanguageDisplay';
import { CellOrganizationsActions } from './cells/CellOrganizationsActions/CellOrganizationsActions';
import { CellBrandsActions } from './cells/CellBrandsActions/CellBrandsActions';
import { CellProducersActions } from './cells/CellProducersActions';
import { CellUsersActions } from './cells/CellUsersActions';
import { CellMeterpointsActions } from './cells/CellMeterpointsActions/CellMeterpointsActions';
import { CellAdditionalitiesActions } from './cells/CellAdditionalitiesActions';
import { useAuthorization } from '@lib/authorization';
import { ROLES } from '@config/constants/roles';
import { ACLS } from '@config/constants/acls';

// Entities & constants
const ColumnType = {
	ShortText: 'shortText',
	Text: 'text',
	Tags: 'tags',
	Expand: 'expand',
	Language: 'language',
	TextWithChevron: 'textWithChevron',
	MintStatus: 'mintStatus',
	QuantityCheck: 'QuantityCheck',
	ConversionFactorStatus: 'conversionFactorStatus',
	VerificationStatus: 'verificationStatus',
	ObfuscateIPFSStatus: 'obfuscateIPFSStatus',
	ActiveStatus: 'activeStatus',
	ProjectAdditionalityUnlimited:
		'projectAdditionalityUnlimited',
	UsedStatus: 'usedStatus',
	ProductionsActions: 'productionsActions',
	ProductionsStartMintActions:
		'productionsStartMintActions',
	ProductionsStartMintWithoutRemoveActions:
		'productionsStartMintWithoutRemoveActions',
	ApiTokensActions: 'apiTokensActions',
	ConversionFactorUsedStatus:
		'conversionFactorUsedStatus',
	OffsetsActions: 'offsetsActions',
	PublicOffsetsActions: 'publicOffsetsActions',
	ConversionFactorsActions: 'conversionFactorsActions',
	MeasurabilitiesActions: 'measurabilitiesActions',
	AdditionalitiesActions: 'additionalitiesActions',
	SitesActions: 'sitesActions',
	UsersActions: 'usersActions',
	OrganizationsActions: 'organizationsActions',
	BrandsActions: 'brandsActions',
	ProducersActions: 'producersActions',
	UserStatus: 'userStatus',
	MeterpointsActions: 'meterpointsActions',
};
type EntityStatus =
	| 'PENDING'
	| 'COMPLETED'
	| 'IN_PROGRESS';
type Language = 'fr' | 'en';

type TCertificate = {
	createdBy: string;
	offset: string;
	version: string;
	language: Language;
	status: EntityStatus;
	organization: string;
	entity: string;
	createdAt: string;
	updatedAt: string;
	id: string;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Datatable = (props: any) => {
	const {
		columns,
		onVerify,
		onChangeObscureIPFS,
		onMint,
		onRemoveItem,
		onEditItem,
		onViewMore,
		onDownload,
		handleUpdateColumns,
		allColumns,
		onActive,
		handleSendCertificate,
		onChangeUserStatus,
		...restProps
	} = props;

	// Selected rows
	const [selectedRecords, setSelectedRecords] = useState(
		[]
	);

	const { t } = useTranslation();
	const { checkRole } = useAuthorization();
	const adaptedColumns =
		columns &&
		columns.map((c: any) => {
			// Get information of each column
			const adaptedCol = { ...c };
			switch (c.type) {
				case ColumnType.VerificationStatus: {
					adaptedCol.render = function render(row: any) {
						return (
							<VerifiedButton
								readOnly={
									!checkRole([
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.STAFF,
									])
								}
								data-testid={row.id + '-testid'}
								onClick={(
									event: React.MouseEvent<HTMLElement>
								) => {
									if (
										!row?.aco2Token &&
										checkRole([
											ROLES.ADMIN,
											ROLES.SUPERADMIN,
											ROLES.STAFF,
										])
									) {
										event.stopPropagation();
										onVerify(row);
									}
								}}
								isverified={
									row && row.verified
										? row.verified.toString()
										: undefined
								}
							>
								{row && row.verified
									? t('global.verified')
									: t('global.notVerified')}
							</VerifiedButton>
						);
					};
					break;
				}

				case ColumnType.ObfuscateIPFSStatus: {
					adaptedCol.render = function render(row: any) {
						return (
							<Group gap="4px">
								<Text
									className={clsx(
										classes.dot,
										!row.obfuscateIPFS
											? classes.successColor
											: classes.errorColor
									)}
								>
									●
								</Text>

								<Text
									className={clsx(
										classes.text,
										!row.obfuscateIPFS
											? classes.successColor
											: classes.errorColor
									)}
								>
									{!row.obfuscateIPFS
										? t(`global.shared`)
										: t(`global.notShared`)}
								</Text>
							</Group>
						);
					};
					break;
				}

				case ColumnType.ProjectAdditionalityUnlimited: {
					adaptedCol.render = function render(row: any) {
						return (
							<span>
								{row.unlimited
									? t('global.unlimited')
									: t('global.limited')}
							</span>
						);
					};
					break;
				}

				case ColumnType.ActiveStatus: {
					adaptedCol.render = function render(row: any) {
						return (
							<VerifiedButton
								onClick={(event: any) => {
									event.stopPropagation();
									onActive(row);
								}}
								data-testid={row.id + '-testid'}
								isverified={
									row && row.active
										? row.active.toString()
										: undefined
								}
							>
								{row && row.active
									? t('global.active')
									: t('global.inactive')}
							</VerifiedButton>
						);
					};
					break;
				}

				case ColumnType.UsedStatus: {
					adaptedCol.render = function render(row: any) {
						return (
							<Tooltip
								label={
									row && row.isUsed
										? t('conversionFactors.usedOnMint')
										: t('conversionFactors.notUsedOnMint')
								}
								isActive={true}
							>
								<Box
									className={classes.containerUserStatus}
								>
									<VerifiedButton
										data-testid={row.id + '-testid'}
										onClick={(event: any) => {
											if (!row?.aco2Token) {
												event.stopPropagation();
												onVerify(row);
											}
										}}
										isverified={
											row && row.isUsed
												? row.isUsed.toString()
												: undefined
										}
									>
										{row && row.isUsed
											? t('global.used')
											: t('global.notUsed')}
									</VerifiedButton>
								</Box>
							</Tooltip>
						);
					};
					break;
				}

				case ColumnType.ConversionFactorUsedStatus: {
					adaptedCol.render = function render(row: any) {
						return (
							<NativeTooltip
								label={
									row && row.isUsed
										? t('conversionFactors.usedOnMint')
										: t('conversionFactors.notUsedOnMint')
								}
							>
								<Box
									className={classes.containerUserStatus}
								>
									<VerifiedButton
										data-testid={row.id + '-testid'}
										isverified={
											row && row.isUsed
												? row.isUsed.toString()
												: undefined
										}
									>
										{row && row.isUsed
											? t('global.used')
											: t('global.notUsed')}
									</VerifiedButton>
								</Box>
							</NativeTooltip>
						);
					};
					break;
				}

				case ColumnType.UserStatus: {
					adaptedCol.render = function render(row: any) {
						return (
							<Box
								className={classes.containerUserStatus}
							>
								<VerifiedButton
									data-testid={row.id + '-testid'}
									onClick={(e) => {
										e.stopPropagation();
										onChangeUserStatus(row);
									}}
									isverified={
										row && !row?.disabled
											? row.disabled.toString()
											: undefined
									}
								>
									{row && row.disabled
										? t('global.inactive')
										: t('global.active')}
								</VerifiedButton>
							</Box>
						);
					};
					break;
				}

				case ColumnType.TextWithChevron: {
					adaptedCol.render = function render(row: any) {
						return (
							<>
								<IconChevronRight
									className={clsx(
										classes.icon,
										classes.expandIcon,
										{
											[classes.expandIconRotated]:
												restProps.expandedids.includes(
													row.id
												),
										}
									)}
								/>

								<span>{row[adaptedCol.accessor]}</span>
							</>
						);
					};
					break;
				}

				case ColumnType.ShortText: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellShortText text={row[c.accessor]} />
						);
					};
					break;
				}

				case ColumnType.MintStatus: {
					adaptedCol.render = function render(row: any) {
						return <CellMintStatus row={row} />;
					};
					break;
				}

				case ColumnType.QuantityCheck: {
					adaptedCol.render = function render(row: any) {
						return <CellOverrangeStatus row={row} />;
					};
					break;
				}

				case ColumnType.ConversionFactorStatus: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellConversionFactorStatus row={row} />
						);
					};
					break;
				}

				case ColumnType.ProductionsActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellProductionsActions
								onDelete={(data) => {
									onRemoveItem(data);
								}}
								onVerify={(data) => {
									onVerify(data);
								}}
								onMint={(data) => {
									onMint(data);
								}}
								onViewMore={(data) => {
									onViewMore(data);
								}}
								row={row}
							/>
						);
					};
					break;
				}

				case ColumnType.ProductionsStartMintActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellProductionsStartMintActions
								onViewMore={(data) => {
									onViewMore(data);
								}}
								onDelete={(data) => {
									onRemoveItem(data);
								}}
								row={row}
							/>
						);
					};
					break;
				}
				case ColumnType.ProductionsStartMintWithoutRemoveActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellProductionsStartMintWithoutRemoveActions
								onViewMore={(data) => {
									onViewMore(data);
								}}
								row={row}
							/>
						);
					};
					break;
				}

				case ColumnType.Tags: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellTags
								tags={row && row.tags ? row.tags : []}
							/>
						);
					};
					break;
				}

				case ColumnType.Expand: {
					adaptedCol.render = function render(row: any) {
						return (
							<IconChevronRight
								className={clsx(
									classes.icon,
									classes.expandIcon,
									{
										[classes.expandIconRotated]:
											restProps.expandedids.includes(
												row.id
											),
									}
								)}
							/>
						);
					};
					break;
				}

				case ColumnType.Language: {
					adaptedCol.render = function render(row: any) {
						return (
							<LanguageDisplay
								language={row?.language || 'en'}
							/>
						);
					};
					break;
				}

				case ColumnType.PublicOffsetsActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellPublicOffsetsActions
								handledownload={(
									id: string,
									lang: string
								) => {
									const nameOffset =
										row && row.name
											? row.name
											: NOT_APPLICABLE;
									const nameBrand =
										row && row.brand
											? row.brand
											: NOT_APPLICABLE;
									const contributionDate =
										row && row.contributionDate
											? row.contributionDate
											: NOT_APPLICABLE;

									onDownload(
										id,
										nameOffset,
										nameBrand,
										contributionDate,
										lang
									);
								}}
								handleviewmore={(data: TOffset) => {
									onViewMore(data);
								}}
								row={row}
							/>
						);
					};
					break;
				}

				case ColumnType.OffsetsActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellOffsetsActions
								handleSendCertificate={(
									data: TOffset | undefined
								) => {
									props.handleSendCertificate(data);
								}}
								handledownload={(
									id: string,
									lang: string
								) => {
									const nameOffset =
										row && row.name
											? row.name
											: NOT_APPLICABLE;
									const nameBrand =
										row && row.brand
											? row.brand
											: NOT_APPLICABLE;
									const contributionDate =
										row && row.contributionDate
											? row.contributionDate
											: NOT_APPLICABLE;

									let idCertificate = null;

									if (
										row &&
										!isNil(row) &&
										!isNil(row.certificates) &&
										Array.isArray(row.certificates) &&
										row.certificates.length > 0
									) {
										const foundCertificate =
											row.certificates.find(
												(certificate: TCertificate) =>
													certificate.language == lang
											);
										if (!foundCertificate) {
											idCertificate = '';
										} else {
											idCertificate = foundCertificate.id;
										}
									}
									let certificates = [];

									if (
										row &&
										!isNil(row) &&
										!isNil(row.certificates) &&
										Array.isArray(row.certificates)
									) {
										certificates = row.certificates;
									}

									onDownload(
										id,
										nameOffset,
										nameBrand,
										contributionDate,
										idCertificate,
										lang,
										certificates
									);
								}}
								handleviewmore={(data: TOffset) => {
									onViewMore(data);
								}}
								row={row}
							/>
						);
					};
					break;
				}
				case ColumnType.MeasurabilitiesActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellMeasurabilitiesActions
								onDelete={onRemoveItem}
								onViewMore={onViewMore}
								onEdit={onEditItem}
								row={row}
							/>
						);
					};
					break;
				}
				case ColumnType.AdditionalitiesActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellAdditionalitiesActions
								onDelete={onRemoveItem}
								onViewMore={onViewMore}
								onEdit={onEditItem}
								row={row}
							/>
						);
					};
					break;
				}

				case ColumnType.ConversionFactorsActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellConversionFactorsActions
								onDelete={onRemoveItem}
								onViewMore={onViewMore}
								onEdit={onEditItem}
								row={row}
							/>
						);
					};
					break;
				}
				case ColumnType.MeterpointsActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellMeterpointsActions
								onDelete={onRemoveItem}
								onViewMore={onViewMore}
								onEdit={onEditItem}
								row={row}
							/>
						);
					};
					break;
				}
				case ColumnType.SitesActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellSitesActions
								onDelete={onRemoveItem}
								onViewMore={onViewMore}
								onEdit={onEditItem}
								row={row}
							/>
						);
					};
					break;
				}
				case ColumnType.UsersActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellUsersActions
								onDelete={onRemoveItem}
								onViewMore={onViewMore}
								onEdit={onEditItem}
								row={row}
							/>
						);
					};
					break;
				}
				case ColumnType.OrganizationsActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellOrganizationsActions
								onDelete={onRemoveItem}
								onViewMore={onViewMore}
								onEdit={onEditItem}
								row={row}
							/>
						);
					};
					break;
				}

				case ColumnType.BrandsActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellBrandsActions
								onDelete={onRemoveItem}
								onViewMore={onViewMore}
								onEdit={onEditItem}
								row={row}
							/>
						);
					};
					break;
				}
				case ColumnType.ProducersActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellProducersActions
								onDelete={onRemoveItem}
								onViewMore={onViewMore}
								onEdit={onEditItem}
								row={row}
							/>
						);
					};
					break;
				}

				case ColumnType.ApiTokensActions: {
					adaptedCol.render = function render(row: any) {
						return (
							<CellApiTokensActions
								onDelete={(data) => {
									onRemoveItem(data);
								}}
								onViewMore={(data) => {
									onViewMore(data);
								}}
								row={row}
							/>
						);
					};
					break;
				}
				default:
					break;
			}

			return adaptedCol.accessor == 'actions'
				? {
						...adaptedCol,

						title: (
							<CellGlobalActions
								disabled={
									handleUpdateColumns ? false : true
								}
								allColumns={allColumns}
								columns={columns}
								handlechangecolumns={(columns) => {
									if (handleUpdateColumns)
										handleUpdateColumns(columns);
								}}
							/>
						),
					}
				: {
						...adaptedCol,
						title: t(
							adaptedCol.title ? adaptedCol.title : ''
						),
					};
		});

	const handleemptystate = () => {
		return (
			<Box className={classes.containerTextsNoRecords}>
				<Image
					src={NoRecordsIcon}
					className={
						restProps.type == 'productionStartMint'
							? classes.iconNoRecordsMintHistory
							: classes.iconNoRecords
					}
				/>
				{restProps.type == 'productionStartMint' ? (
					<>
						<Text className={classes.titleNoRecords}>
							{t('global.datatable.noProductions')}
						</Text>
						<Text
							className={classes.descriptionNoRecords}
						>
							{t(
								'global.datatable.startBySelectingAproject'
							)}
						</Text>
					</>
				) : (
					<>
						<Text className={classes.titleNoRecords}>
							{t('global.datatable.noResultsFound')}
						</Text>
						<Text
							className={classes.descriptionNoRecords}
						>
							{t(
								'global.datatable.noResultsFoundDescription'
							)}
						</Text>
					</>
				)}
			</Box>
		);
	};

	/* ******************************** RENDERING ******************************* */

	return (
		<NativeDataTable
			id="V1StGXR8_Z5jdHi6B-datatable"
			height={props.height}
			rowClassName={() => classes.customRow}
			classNames={classes}
			striped
			columns={adaptedColumns}
			records={props.data}
			fetching={props.loading}
			sortStatus={props.sortstatus}
			onSortStatusChange={props.onSortStatusChange}
			selectedRecords={selectedRecords}
			onSelectedRecordsChange={setSelectedRecords}
			emptyState={handleemptystate()}
			{...restProps}
		/>
	);
};
